/**
 * VoiceRecordModal (FAN) — record and send a short voice note.
 *
 * Hard limits: 10-second auto-stop, and the backend enforces max 3 per day. Uploads directly
 * to POST /api/messages/voice-note; the message is delivered live over the socket, same as any
 * other message (playback already works via AttachmentDisplay).
 */

import { useState, useRef, useEffect, useCallback } from 'react'

const MAX_SECONDS = 10
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

type RecordingState = 'idle' | 'recording' | 'preview' | 'sending'

interface VoiceRecordModalProps {
  isOpen: boolean
  onClose: () => void
  onSent?: (remainingToday: number) => void
}

export default function VoiceRecordModal({ isOpen, onClose, onSent }: VoiceRecordModalProps) {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle')
  const [duration, setDuration] = useState(0)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [remaining, setRemaining] = useState<number | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    if (audioUrl) URL.revokeObjectURL(audioUrl)
  }, [audioUrl])

  const resetState = () => {
    setRecordingState('idle'); setDuration(0); setAudioBlob(null); setAudioUrl(null); setError(null)
    chunksRef.current = []
  }

  useEffect(() => () => cleanup(), [cleanup])

  // Load remaining-today when opened; reset when closed.
  useEffect(() => {
    if (!isOpen) { cleanup(); resetState(); return }
    const token = localStorage.getItem('auth_token')
    fetch(`${API_URL}/api/messages/voice-note/limit`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setRemaining(d.remaining_today) })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const getSupportedMimeType = (): string => {
    const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg']
    for (const t of types) { if (MediaRecorder.isTypeSupported(t)) return t }
    return 'audio/webm'
  }

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
  }, [])

  // Hard 10s auto-stop
  useEffect(() => {
    if (recordingState === 'recording' && duration >= MAX_SECONDS) stopRecording()
  }, [duration, recordingState, stopRecording])

  const startRecording = async () => {
    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = getSupportedMimeType()
      const mr = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = mr
      chunksRef.current = []
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        setAudioUrl(URL.createObjectURL(blob))
        setRecordingState('preview')
        if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
      }
      mr.start(100)
      setRecordingState('recording')
      setDuration(0)
      timerRef.current = window.setInterval(() => setDuration(prev => prev + 1), 1000)
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        setError('Microphone access denied. Allow microphone access in your browser settings.')
      } else if (err?.name === 'NotFoundError') {
        setError('No microphone found. Connect one and try again.')
      } else {
        setError('Could not start recording. Please try again.')
      }
    }
  }

  const reRecord = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioBlob(null); setAudioUrl(null); setDuration(0); setRecordingState('idle'); setError(null)
  }

  const handleSend = async () => {
    if (!audioBlob) return
    setRecordingState('sending'); setError(null)
    try {
      const token = localStorage.getItem('auth_token')
      const type = audioBlob.type || 'audio/webm'
      const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm'
      const form = new FormData()
      form.append('file', audioBlob, `voice_${Date.now()}.${ext}`)
      form.append('duration_seconds', String(duration))
      const resp = await fetch(`${API_URL}/api/messages/voice-note`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      })
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        throw new Error(data.error || 'Failed to send voice note.')
      }
      const data = await resp.json()
      onSent?.(data.remaining_today ?? 0)
      onClose()
    } catch (err: any) {
      setError(err?.message || 'Failed to send. Please try again.')
      setRecordingState('preview')
    }
  }

  if (!isOpen) return null

  const outOfNotes = remaining !== null && remaining <= 0
  const fmt = (s: number) => `0:${String(s).padStart(2, '0')}`

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Voice note</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Up to 10 seconds{remaining !== null ? ` · ${remaining} of 3 left today` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={recordingState === 'sending'} aria-label="Close">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-6">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

          {outOfNotes && recordingState === 'idle' ? (
            <div className="text-center py-6 text-gray-600 text-sm">
              You've sent all 3 voice notes for today. Try again tomorrow.
            </div>
          ) : (
            <>
              <div className="bg-gray-50 rounded-xl p-6 mb-5">
                {recordingState === 'recording' ? (
                  <div className="flex items-end justify-center gap-0.5 h-20">
                    {[...Array(28)].map((_, i) => (
                      <div key={i} className="w-1 bg-pink-500 rounded-full"
                        style={{ height: `${25 + Math.abs(Math.sin(Date.now() / 120 + i)) * 60}%` }} />
                    ))}
                  </div>
                ) : (recordingState === 'preview' || recordingState === 'sending') ? (
                  <audio src={audioUrl || undefined} controls className="w-full" />
                ) : (
                  <div className="flex items-center justify-center h-20">
                    <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-center text-2xl font-mono text-gray-700 mb-5 tabular-nums">
                {fmt(duration)} <span className="text-sm text-gray-400">/ 0:10</span>
              </div>

              <div className="flex gap-3">
                {recordingState === 'idle' && (
                  <button onClick={startRecording} className="flex-1 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-medium flex items-center justify-center gap-2">
                    <span className="w-3 h-3 bg-white rounded-full animate-pulse" /> Start recording
                  </button>
                )}
                {recordingState === 'recording' && (
                  <button onClick={stopRecording} className="flex-1 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-xl font-medium flex items-center justify-center gap-2">
                    <span className="w-3 h-3 bg-white" /> Stop
                  </button>
                )}
                {recordingState === 'preview' && (
                  <>
                    <button onClick={reRecord} className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium">Re-record</button>
                    <button onClick={handleSend} className="flex-1 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-medium">Send</button>
                  </>
                )}
                {recordingState === 'sending' && (
                  <div className="flex-1 py-3 bg-gray-100 text-gray-500 rounded-xl font-medium text-center flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Sending…
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
